<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'

const props = withDefaults(
  defineProps<{
    /** Renders the row as a link to this route; it washes on hover. */
    to?: RouteLocationRaw
    /** Without `to`: `div` (default), a static row that never washes · `button`, an action row that washes; bind `@click` · `label`, a row that washes and wraps its own form control. */
    as?: 'div' | 'button' | 'label'
    /** Padding: `default` 16px all round · `compact` 12px block, 16px inline, for a single-line row. */
    density?: 'default' | 'compact'
    /** Marks the row as the current pick; it holds the accent tint and does not wash. */
    selected?: boolean
  }>(),
  { to: undefined, as: 'div', density: 'default', selected: false },
)

defineOptions({ inheritAttrs: false })

defineSlots<{
  /** The row's content; the row draws only the box, so its layout is the caller's. */
  default(): unknown
  /** Actions beside the row, outside its click target; the row and the actions then sit in one wrapper. */
  end?: () => unknown
}>()

const interactive = computed(() => props.to !== undefined || props.as !== 'div')

const rowClass = computed(() => [
  'block w-full min-w-0 rounded-4 text-left',
  props.density === 'compact' ? 'px-8 py-6' : 'p-8',
  props.selected && 'bg-accent-blue/10',
  interactive.value && [
    'cursor-pointer outline-none transition-colors duration-fast ease-soft motion-reduce:transition-none',
    !props.selected && 'hovered:bg-bg-wash',
    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid focus-visible:outline-accent-blue',
  ],
])
</script>

<template>
  <div v-if="$slots.end" class="flex items-center gap-2">
    <component
      :is="to !== undefined ? RouterLink : as"
      v-bind="$attrs"
      :to="to"
      :type="to === undefined && as === 'button' ? 'button' : undefined"
      :class="[rowClass, 'flex-1']"
    >
      <slot />
    </component>
    <div class="flex shrink-0 items-center pe-4">
      <slot name="end" />
    </div>
  </div>
  <component
    :is="to !== undefined ? RouterLink : as"
    v-else
    v-bind="$attrs"
    :to="to"
    :type="to === undefined && as === 'button' ? 'button' : undefined"
    :class="rowClass"
  >
    <slot />
  </component>
</template>
